import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Distributor } from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { SearchInput } from '@/components/SearchInput';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { useDistributors } from '@/data/hooks';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';

const DISTRIBUTOR_SORT: SortOption<Distributor>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((d) => d.createdAt, 'desc') },
  { value: 'az', label: 'Nombre A–Z', compare: byText((d) => d.name) },
  { value: 'za', label: 'Nombre Z–A', compare: byText((d) => d.name, 'desc') },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<Distributor>(
      (a, b) => statusLabel('account', a.status).localeCompare(statusLabel('account', b.status), 'es'),
      byText((d) => d.name),
    ),
  },
];

const BASE = '/app/admin/distribuidores';

/**
 * Distribuidores list (admin). Mirrors the Clientes/Productos list pattern:
 * fast client-side search, a desktop table + mobile cards, account status pill,
 * and links into the detail screen. Wholesale accounts carry territories and
 * source locations rather than credit/balance, so those columns are shown here.
 */
export function DistributorsListPage() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const isAdmin = useIsAdmin();
  const query = useDistributors({ limit: 100 });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortItems(items, DISTRIBUTOR_SORT, sort);
    return sortItems(
      items.filter(
        (d) => d.name.toLowerCase().includes(q) || d.rif.toLowerCase().includes(q),
      ),
      DISTRIBUTOR_SORT,
      sort,
    );
  }, [items, search, sort]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Distribuidores"
        description="Cuentas mayoristas, territorios y ubicaciones."
        actions={
          isAdmin ? (
            <Link to={`${BASE}/nuevo`}>
              <Button size="sm">Nuevo distribuidor</Button>
            </Link>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nombre o RIF…"
            />
          </div>
          <SortSelect options={DISTRIBUTOR_SORT} value={sort} onChange={setSort} />
        </div>

        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={filtered.length === 0}
          loadingLabel="Cargando distribuidores…"
          loadingVariant="table"
          skeletonCols={4}
          emptyTitle={search ? 'Sin coincidencias' : 'No hay distribuidores'}
          emptyDescription={
            search
              ? 'Ajusta la búsqueda para ver más resultados.'
              : 'Aún no se han registrado distribuidores.'
          }
          emptyAction={
            !search && isAdmin ? (
              <Link to={`${BASE}/nuevo`}>
                <Button size="sm">Nuevo distribuidor</Button>
              </Link>
            ) : undefined
          }
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Distribuidor</th>
                  <th className="px-4 py-3 font-medium">RIF</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Territorios</th>
                  <th className="px-4 py-3 font-medium">Ubicaciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr
                    key={d.distributorId}
                    className="border-b border-border last:border-0 hover:bg-surface-muted"
                  >
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to={`${BASE}/${d.distributorId}`}
                        className="text-brand-primary hover:underline"
                      >
                        {d.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-text-muted">{d.rif}</td>
                    <td className="px-4 py-3">
                      <StatusPill kind="account" value={d.status} />
                    </td>
                    <td className="px-4 py-3 text-text-muted">{countLabel(d.territoryIds.length)}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {countLabel(d.allowedLocationIds.length, 'ubicación', 'ubicaciones')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {filtered.map((d) => (
              <li key={d.distributorId} className="p-4">
                <Link
                  to={`${BASE}/${d.distributorId}`}
                  className="-m-4 block p-4 active:bg-surface-muted"
                >
                  <DistributorCardBody distributor={d} />
                </Link>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

function DistributorCardBody({ distributor: d }: { distributor: Distributor }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-text-primary">{d.name}</p>
          <p className="text-xs text-text-muted">{d.rif}</p>
        </div>
        <StatusPill kind="account" value={d.status} />
      </div>
      <p className="text-xs text-text-muted">
        {countLabel(d.territoryIds.length)} · {countLabel(d.allowedLocationIds.length, 'ubicación', 'ubicaciones')}
      </p>
    </div>
  );
}

function countLabel(n: number, singular = 'territorio', plural = 'territorios'): string {
  if (n === 0) return `Sin ${plural}`;
  return `${n} ${n === 1 ? singular : plural}`;
}
